import {Component, inject, OnDestroy, OnInit} from "@angular/core";
import {Store} from "@ngrx/store";
import {CommonModule} from "@angular/common";
import {FormBuilder, ReactiveFormsModule} from "@angular/forms";
import {MatButtonModule} from "@angular/material/button";
import {MatCardModule} from "@angular/material/card";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatIconModule} from "@angular/material/icon";
import {MatInputModule} from "@angular/material/input";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {SharedModule} from "../../../shared/shared.module";
import {CardCommentsButtonComponent} from "../../../shared/comments/card-comments-button/card-comments-button.component";
import {CardContextMenuDirective} from "../../../shared/comments/card-context-menu/card-context-menu.directive";
import {map, Subscription} from "rxjs";
import {DevelopmentsSinceInvestmentActions} from "../../store/asset.actions";
import {
    selectAssetCommentaryHistory,
    selectDevelopmentsSinceInvestment,
    selectDevelopmentsSinceInvestmentEdit,
    selectDevelopmentsSinceInvestmentSaveState,
    selectIsDevelopmentsSinceInvestmentEditDisabled,
    selectRelatedReportId,
    selectReportDate
} from "../../store/asset/asset.selectors";

@Component({
    selector: "valumize-asset-developments-since-investment",
    templateUrl: "./asset-developments-since-investment.component.html",
    styleUrls: ["./asset-developments-since-investment.component.scss"],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        // InfoPanelComponent and the timestamp pipe are still NgModule-declared, so they come in via
        // SharedModule rather than individually (SharedModule imports no feature module, so there is
        // no cycle). Everything already standalone is imported directly.
        SharedModule,
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatButtonModule,
        MatProgressSpinnerModule,
        CardCommentsButtonComponent,
        CardContextMenuDirective
    ]
})
export class AssetDevelopmentsSinceInvestmentComponent implements OnInit, OnDestroy {
    private readonly store = inject(Store);
    private readonly formBuilder = inject(FormBuilder);

    saveState$ = this.store.select(selectDevelopmentsSinceInvestmentSaveState);
    developmentsSinceInvestment$ = this.store.select(selectDevelopmentsSinceInvestment);
    // Shared with the recent-financial-performance card: one request, each card reads its own field.
    commentaryHistory$ = this.store.select(selectAssetCommentaryHistory);
    relatedReportId$ = this.store.select(selectRelatedReportId);
    // The current report's date, so the editable entry can label itself without depending on the
    // history read-model having loaded.
    reportDate$ = this.store.select(selectReportDate);
    // Covers the page-wide one-form-at-a-time guard AND the card's own rule that this
    // forward-looking commentary is only editable while the investment is ongoing. The history
    // renders regardless, so a realized investment's record stays readable.
    isEditDisabled$ = this.store.select(selectIsDevelopmentsSinceInvestmentEditDisabled);

    isEditable = false;
    // What the server holds, so cancel() can put the form back rather than leaving the draft.
    private persistedText = "";
    subscriptions: Subscription[] = [];

    developmentsSinceInvestmentForm = this.formBuilder.group({
        text: this.formBuilder.control("", {nonNullable: true})
    });

    ngOnInit() {
        this.subscriptions.push(
            this.store.select(selectDevelopmentsSinceInvestmentEdit).pipe(map((isEditable) => {
                this.isEditable = isEditable;
                if (isEditable) {
                    this.developmentsSinceInvestmentForm.enable();
                } else {
                    // Leaving edit mode discards whatever was typed. Cancel only flips this flag and
                    // never touches generalPartnerValuation.data, so the store emits nothing and the
                    // patch subscription below cannot fire — without this the abandoned draft stayed
                    // in the control, invisible (the read-only view renders from the store) until the
                    // next Edit, and the next Save persisted it.
                    this.developmentsSinceInvestmentForm.patchValue({text: this.persistedText});
                    this.developmentsSinceInvestmentForm.disable();
                }
            })).subscribe());

        this.subscriptions.push(
            this.developmentsSinceInvestment$.pipe(
                map((developmentsSinceInvestment) => {
                    // Track unconditionally so cancel() always has the persisted text to restore.
                    this.persistedText = developmentsSinceInvestment.text ?? "";

                    // Patch only when not editing, so a store re-emission (e.g. another card's save
                    // overwriting fundInvestment.data) cannot clobber unsaved input.
                    if (!this.isEditable) {
                        this.developmentsSinceInvestmentForm.patchValue({text: this.persistedText});
                    }
                })
            ).subscribe());
    }

    editMode = () => this.store.dispatch(DevelopmentsSinceInvestmentActions.edit());

    save() {
        if (this.developmentsSinceInvestmentForm.valid) {
            this.store.dispatch(DevelopmentsSinceInvestmentActions.save(this.developmentsSinceInvestmentForm.getRawValue()));
        }
    }

    cancel = () => this.store.dispatch(DevelopmentsSinceInvestmentActions.cancel());

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
